import { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/features/auth/AuthProvider';
import { useCurriculum } from './useCurriculum';
import { isAdmin, isCoach, isCoordinator } from '@/utils/roleChecks';
import type { CurriculumLevel, CurriculumUnit, CurriculumSection, CurriculumLesson, CurriculumTechnique } from '@/types';

/**
 * The member's own syllabus, read-only.
 *
 * BELT MATCHING — the one trap here.
 * `curriculumLevels.associatedBelts` stores lowercase literals:
 *     [{ belt: 'white' }]
 * while `studentProfiles.belt` stores capitalised display values, live today:
 *     White 132 · Blue 9 · Grey White 3 · Purple 3 · Grey 1
 * A naive `===` never matches. Everything is normalised before comparison.
 *
 * Kids' belts ('Grey', 'Grey White', …) have no level authored, so they resolve
 * to null and the nav item stays hidden — which is correct until someone
 * authors a kids syllabus, and is NOT a bug to "fix" by falling back to white.
 */

export interface SyllabusSection extends CurriculumSection {
    lessons: Array<CurriculumLesson & { techniques: CurriculumTechnique[] }>;
    /** Techniques hung directly off the section, if any — see hasContent note. */
    techniques: CurriculumTechnique[];
}
export interface SyllabusUnit extends CurriculumUnit {
    sections: SyllabusSection[];
}
export interface Syllabus {
    level: CurriculumLevel;
    units: SyllabusUnit[];
    /** Leaf content exists somewhere under this level. Drives the nav gate. */
    hasContent: boolean;
    techniqueCount: number;
}

const normaliseBelt = (v: unknown): string =>
    typeof v === 'string' ? v.trim().toLowerCase().replace(/\s+/g, ' ') : '';

/** Exported for tests — the belt→level match, isolated from any I/O. */
export function levelForBelt(levels: CurriculumLevel[], belt: string): CurriculumLevel | null {
    const want = normaliseBelt(belt);
    if (!want) return null;
    for (const lvl of levels) {
        const belts = Array.isArray(lvl.associatedBelts) ? lvl.associatedBelts : [];
        if (belts.some((b) => normaliseBelt(b?.belt) === want)) return lvl;
    }
    return null;
}

export const useMySyllabus = (overrideLevelId?: string) => {
    const { currentUser } = useAuth();
    const { getLevels, getUnits, getSections, getLessons, getTechniques } = useCurriculum();

    const [levels, setLevels] = useState<CurriculumLevel[]>([]);
    const [syllabus, setSyllabus] = useState<Syllabus | null>(null);
    const [belt, setBelt] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const uid = currentUser?.uid;

    const load = useCallback(async () => {
        if (!uid) { setLoading(false); return; }
        setLoading(true);
        setError(false);
        try {
            const profileSnap = await getDoc(doc(db, 'studentProfiles', uid));
            const memberBelt = profileSnap.exists() ? String(profileSnap.data()?.belt ?? '') : '';
            setBelt(memberBelt);

            const all = await getLevels();
            setLevels(all);

            const level = overrideLevelId
                ? all.find((l) => l.id === overrideLevelId) ?? null
                : levelForBelt(all, memberBelt);
            if (!level) { setSyllabus(null); return; }

            const units = await getUnits(level.id);
            const built: SyllabusUnit[] = [];
            let techniqueCount = 0;

            for (const unit of units) {
                const sections = await getSections(unit.id);
                const builtSections: SyllabusSection[] = [];
                for (const section of sections) {
                    const lessons = await getLessons(section.id);
                    const builtLessons = [];
                    for (const lesson of lessons) {
                        const techniques = await getTechniques(lesson.id);
                        techniqueCount += techniques.length;
                        builtLessons.push({ ...lesson, techniques });
                    }
                    builtSections.push({ ...section, lessons: builtLessons, techniques: [] });
                }
                built.push({ ...unit, sections: builtSections });
            }

            setSyllabus({
                level,
                units: built,
                techniqueCount,
                // Leaf content = at least one technique. Deliberately strict: on
                // 2026-09-14 the white belt level had 7 units and 1 section but
                // ZERO techniques of the 121 written down, and a member who opens
                // an empty syllabus once does not open it again.
                hasContent: techniqueCount > 0,
            });
        } catch (err) {
            console.error('[useMySyllabus] load failed', err);
            setError(true);
            setSyllabus(null);
        } finally {
            setLoading(false);
        }
    }, [uid, overrideLevelId, getLevels, getUnits, getSections, getLessons, getTechniques]);

    useEffect(() => { load(); }, [load]);

    return useMemo(
        () => ({ syllabus, levels, belt, loading, error, reload: load }),
        [syllabus, levels, belt, loading, error, load],
    );
};

/**
 * Does this member have a syllabus worth opening? Drives the nav gate.
 *
 * Spec, and it is not negotiable: if the member's belt level has zero techniques
 * the menu item does not appear. "A member who finds an empty page once does not
 * come back to it." With 0 of 121 techniques loaded on 2026-09-14, this gate is
 * doing real work — nothing ships to members until content is in.
 *
 * Memoised per uid for the session: the tree walk is ~30-50 reads and the
 * sidebar renders on every route change. The curriculum is static between
 * authoring sessions, so a per-session answer is correct and a per-render one
 * would be indefensible.
 */
const availabilityCache = new Map<string, Promise<boolean>>();

/** Test seam. */
export function __resetSyllabusAvailability(): void { availabilityCache.clear(); }

export function useHasSyllabus(): boolean {
    const { currentUser } = useAuth();
    const staff = isAdmin(currentUser) || isCoach(currentUser) || isCoordinator(currentUser);
    const { getLevels, getUnits, getSections, getLessons, getTechniques } = useCurriculum();
    const [available, setAvailable] = useState(false);
    const uid = currentUser?.uid;

    useEffect(() => {
        let cancelled = false;
        if (!uid) {
            // Resolve asynchronously rather than setting state synchronously in the
            // effect body — the project lints that as a cascading-render risk.
            Promise.resolve().then(() => { if (!cancelled) setAvailable(false); });
            return () => { cancelled = true; };
        }

        const cacheKey = `${uid}:${staff ? 'staff' : 'member'}`;
        if (!availabilityCache.has(cacheKey)) {
            availabilityCache.set(cacheKey, (async () => {
                try {
                    const profileSnap = await getDoc(doc(db, 'studentProfiles', uid));
                    const belt = profileSnap.exists() ? String(profileSnap.data()?.belt ?? '') : '';
                    const all = await getLevels();
                    // Members are gated on THEIR belt's level. Staff teach from the
                    // whole curriculum and get a level picker on the page, so gating
                    // them on their own belt would hide a view they need — a coach
                    // who happens to be a white belt still teaches blue belts.
                    const scope = staff ? all : [levelForBelt(all, belt)].filter(Boolean) as CurriculumLevel[];
                    if (scope.length === 0) return false;
                    for (const level of scope) {
                        for (const unit of await getUnits(level.id)) {
                            for (const section of await getSections(unit.id)) {
                                for (const lesson of await getLessons(section.id)) {
                                    if ((await getTechniques(lesson.id)).length > 0) return true;
                                }
                            }
                        }
                    }
                    return false;
                } catch (err) {
                    // Fail CLOSED: a read error must not put a broken link in the nav.
                    console.error('[useHasSyllabus] check failed', err);
                    return false;
                }
            })());
        }

        availabilityCache.get(cacheKey)!.then((v) => { if (!cancelled) setAvailable(v); });
        return () => { cancelled = true; };
    }, [uid, staff, getLevels, getUnits, getSections, getLessons, getTechniques]);

    return available;
}
